import moment from "moment-timezone";

const TIME_ZONE =
  import.meta.env.VITE_APP_TIMEZONE || "Asia/Kolkata";
// Normalise a status for display so legacy "Completed" records render as
// "Complete" (the canonical value used across the app). Returns the value
// unchanged for every other status.
export const displayStatus = (status) => {
  if (typeof status !== "string") return status;
  return status.trim().toLowerCase() === "completed" ? "Complete" : status;
};


// --- Who is signed in ------------------------------------------------------
// The stored user has been written by several screens over time, so the role
// turns up under any of half a dozen keys and in any casing ("Super Admin",
// "super-admin", "SUPER_ADMIN"). Normalise once here rather than in each
// caller, or a page ends up locking a control for a role that is really the
// same role spelled differently.
/* The signed-in user as an object, never a throw.
 *
 * localStorage holds a string or null. getItem("user") || {} — which is what
 * the header clock ran — hands JSON.parse an OBJECT on the miss; JSON.parse
 * stringifies that to "[object Object]" and then refuses it as invalid JSON.
 * So the first render after the key went missing took the whole page down with
 * a syntax error about text nobody ever wrote.
 *
 * Missing is a normal state, not a broken one: it is what a signed-out tab
 * looks like, and what any thunk that clears the key leaves behind. A caller
 * after a userId should get {} back and carry on. */
export const getCurrentUser = () => {
  try {
    const raw = localStorage.getItem("user");
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    /* Half-written, or written by a version that stored something else.
       Unreadable either way, which is no more useful than absent. */
    return {};
  }
};

export const getCurrentRole = () => {
  const u = getCurrentUser();
  const raw = u.role ?? u.Role ?? u.roleName ?? u.RoleName ?? u.userRole;
  return String(raw || "").trim().toUpperCase().replace(/[\s-]+/g, "_");
};

/* Roles are stored as SUPER_ADMIN / TEAM_LEADER — fine as a key, wrong on a
   screen. Rendered raw they shout, and the underscore reads as a typo. */
const ROLE_LABELS = {
  SUPER_ADMIN: "Super Admin",
  ADMIN: "Admin",
  TEAM_LEADER: "Team Leader",
  USER: "User",
  VIEWER: "Viewer",
  EDITOR: "Editor",
};

export const formatRole = (role) => {
  const key = String(role || "").trim().toUpperCase().replace(/[\s-]+/g, "_");
  if (!key) return "";
  /* Title-case anything not in the map rather than showing the raw key, so a
     role added later still reads properly without a code change. */
  return (
    ROLE_LABELS[key] ||
    key
      .toLowerCase()
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ")
  );
};

// Admins are not bound by team membership: they may move any task to any
// team. sp_Queries_TeamHandover_Add enforces the same two roles, so a screen
// that asks this and a server that refuses can never disagree.
export const isAdminRole = (role = getCurrentRole()) =>
  role === "ADMIN" || role === "SUPER_ADMIN";

// --- Timestamps ------------------------------------------------------------
/* One way to read a timestamp, everywhere.

   Every screen used to choose between two of them by sniffing the hostname:

     subdomain === "localhost" ? moment(v).tz("Asia/Kolkata") : moment.utc(v)

   That was a workaround for the API sending different values from different
   machines. StartTime and friends are `datetime` columns with no offset, filled
   from GETDATE() on a SQL Server running in Asia/Kolkata — so the digits stored
   ARE Indian wall-clock. The mssql driver (useUTC:false) then read them as the
   API process's local time, which was IST on the dev box and UTC on the host,
   putting live five and a half hours out. The API now pins its own timezone
   (see src/config/env.js there), so one instant comes back from both.

   Which makes the branch not just unnecessary but harmful: it guessed the
   server's clock from the browser's address bar, and there is no reason those
   two should agree. Reading the value as an instant and showing it in IST is
   correct on any host, in any browser timezone. */
export const toAppTime = (value) => moment(value).tz(TIME_ZONE);

export const DATETIME_DISPLAY = "DD MMM YYYY, hh:mm A";

/* `-` rather than an empty cell: a blank looks like the column failed to load,
   and half these timestamps are legitimately unset until somebody starts. The
   fallback is overridable because a grouping key wants "(no date)", not a
   dash that reads like a heading. */
export const formatDateTimeDisplay = (value, fmt = DATETIME_DISPLAY, empty = "-") =>
  value ? toAppTime(value).format(fmt) : empty;

/* For sorting and date filtering. The instant is what orders correctly; the
   old live branch stripped the "Z" and re-parsed, which shifted the value by
   the browser's own offset before comparing it. */
export const toDateValue = (value) => (value ? new Date(value) : null);

/* A SQL DATE column — a day, with no time and no zone — as the day it is.

   JSON has no shape for a bare date, so one arrives as an instant, and WHICH
   instant depends on a driver setting nobody reading this file can see. The
   API connects with `useUTC: false` (src/config/db.js), so tedious builds the
   value with `new Date(2000, 0, days)` — LOCAL midnight on the server. A
   target date stored as 2026-09-05 therefore reaches the browser as

       "2026-09-04T18:30:00.000Z"      (local midnight in IST, in UTC)

   and the obvious `.slice(0, 10)` reads the 4th. That is the whole of the bug
   where picking the 5th saved and then showed the 4th: the write was correct
   and the read moved it back a day, every time.

   Taking the local calendar day in the app's timezone undoes exactly what the
   driver did. It is also right for the other arrangements: a UTC-midnight
   instant (useUTC: true) and a server west of Greenwich both still land on the
   intended day once read in IST.

   A value that is already a bare "YYYY-MM-DD" is returned untouched — reading
   THAT as an instant would make it UTC midnight and start the same slip in the
   other direction. */
export const toDateOnly = (value) => {
  if (!value) return "";

  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  return toAppTime(value).format("YYYY-MM-DD");
};

/* Today, as the same plain "YYYY-MM-DD" a target date is compared against.

   en-CA is not a preference about Canada — it is the one widely-supported
   locale whose short date IS ISO order, which makes this a one-liner instead
   of three getters and two padStarts. Local, deliberately: "is this task due
   today" is asked in the reader's day, not in UTC. */
export const todayISO = () => new Date().toLocaleDateString("en-CA");

/* Where a task stands against its target date: "" | "due" | "overdue".
 *
 * "" covers three different innocent cases on purpose — no target date set, a
 * date still in the future, and a task that is finished. A closed task cannot
 * be late: it was or it was not, and colouring a completed row red says
 * something needs doing when nothing does.
 *
 * Pass the status wherever the list has one. Omitting it treats the task as
 * open, which is the safe reading for a list that only ever holds open work.
 */
export const targetDateState = (targetDate, status) => {
  const day = toDateOnly(targetDate);
  if (!day) return "";
  if (status !== undefined && !isPendingStatus(status)) return "";

  const today = todayISO();
  if (day < today) return "overdue";
  if (day === today) return "due";
  return "";
};

/* How many days past the target, for a row that is overdue. Whole days between
   two calendar dates, so it does not drift with the hour of the day. */
export const daysPastTarget = (targetDate) => {
  const day = toDateOnly(targetDate);
  if (!day) return 0;

  const then = new Date(day + "T00:00:00");
  const now = new Date(todayISO() + "T00:00:00");
  const diff = Math.round((now - then) / 86400000);
  return diff > 0 ? diff : 0;
};

/* What to say about it. "Due today" and "3 days over" — the number is the
   point once a task is late, and "overdue" on its own does not carry it. */
export const targetDateLabel = (targetDate, status) => {
  const state = targetDateState(targetDate, status);
  if (state === "due") return "Due today";
  if (state !== "overdue") return "";

  const n = daysPastTarget(targetDate);
  return n === 1 ? "1 day over" : n + " days over";
};

/* The same day, written for a person. Parsed at LOCAL midnight rather than
   through Date(value) for the reason above. */
export const formatDateOnly = (value, empty = "-") => {
  const iso = toDateOnly(value);
  if (!iso) return empty;

  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? empty
    : d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

/* Is this user account still active?

   Deactivating somebody was not enough to get them out of the pickers, and the
   reason is that /user/list deliberately returns EVERY account — the Users
   screen has to list the inactive ones so they can be turned back on. So each
   picker has to filter, and the filters were written as `Status !== 0`.

   That test is wrong here: tedious maps a SQL `bit` to a JavaScript boolean, so
   an inactive account arrives as `false`, and `false !== 0` is true. Kamalesh
   had been deactivated for a while and was still on every dropdown because of
   it.

   Accepts whatever shape the value turns up in — boolean, bit, or string — and
   treats a missing Status as active, since absence is not a signal. */
export const isActiveUser = (user) => {
  const v = user?.Status ?? user?.status;
  if (v === undefined || v === null || v === "") return true;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  const t = String(v).trim().toLowerCase();
  return !["0", "false", "no", "inactive", "n"].includes(t);
};

export const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  // Use regex to find only the numbers to avoid issues with spaces or extra colons
  const parts = timeStr.split(':').map(part => part.trim());
  const hours = parseInt(parts[0], 10) || 0;
  const minutes = parseInt(parts[1], 10) || 0;
  return (hours * 60) + minutes;
};


// Function to generate a unique HSL color based on a string (User Name)
/* The default window for every date-range filter: today and the six days
   before it — a full week including today, not seven days ago.

   Kept here rather than repeated per page because it had already drifted:
   two pages built the range with one set of key names and then read it with
   another, so their default was invisible and their first fetch went out
   unfiltered. One definition, one shape. */
/* Minutes as HH:MM, for reports. 113 -> "01:53", 2315 -> "38:35".
   Hours are NOT wrapped at 24: a sub-total of a week's work is legitimately
   "38:35", and rolling it round to "14:35" would quietly lose a day and a half.

   Distinct from minutesToHHMM below, which pads with spaces ("01 : 10") for
   the grids' duration editor and is parsed back by timeToMinutes. */
export const durationHHMM = (minutes) => {
  const total = Math.round(Number(minutes) || 0);
  const sign = total < 0 ? "-" : "";
  const abs = Math.abs(total);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${sign}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/* =====================================================================
   Report scope: the date window, and pending vs everything
   ===================================================================== */

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay   = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

/* The quick ranges, in the order a reader scans them: nearest first. Each
   returns { start, end } so the pages can drop it straight into state.

   Month arithmetic goes through day 0 of the following month rather than
   "+1 month, -1 day" — setMonth on the 31st rolls into the next month, which
   is how "Last Month" silently becomes a five-week window in March. */
export const DATE_PRESETS = [
  {
    key: "today",
    label: "Today",
    icon: "bi-calendar-event",
    range: () => ({ start: startOfDay(new Date()), end: endOfDay(new Date()) }),
  },
  {
    key: "yesterday",
    label: "Yesterday",
    icon: "bi-clock-history",
    range: () => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return { start: startOfDay(d), end: endOfDay(d) };
    },
  },
  {
    key: "week",
    label: "Last 7 Days",
    icon: "bi-calendar-week",
    range: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 6);
      return { start: startOfDay(start), end: endOfDay(end) };
    },
  },
  {
    key: "thisMonth",
    label: "This Month",
    icon: "bi-calendar3",
    range: () => {
      const n = new Date();
      return {
        start: startOfDay(new Date(n.getFullYear(), n.getMonth(), 1)),
        end: endOfDay(new Date(n.getFullYear(), n.getMonth() + 1, 0)),
      };
    },
  },
  {
    key: "lastMonth",
    label: "Last Month",
    icon: "bi-calendar2",
    range: () => {
      const n = new Date();
      return {
        start: startOfDay(new Date(n.getFullYear(), n.getMonth() - 1, 1)),
        end: endOfDay(new Date(n.getFullYear(), n.getMonth(), 0)),
      };
    },
  },
  {
    key: "3months",
    label: "Last 3 Months",
    icon: "bi-calendar-range",
    range: () => {
      const n = new Date();
      return {
        start: startOfDay(new Date(n.getFullYear(), n.getMonth() - 2, 1)),
        end: endOfDay(new Date(n.getFullYear(), n.getMonth() + 1, 0)),
      };
    },
  },
  {
    key: "6months",
    label: "Last 6 Months",
    icon: "bi-calendar-range",
    range: () => {
      const n = new Date();
      return {
        start: startOfDay(new Date(n.getFullYear(), n.getMonth() - 5, 1)),
        end: endOfDay(new Date(n.getFullYear(), n.getMonth() + 1, 0)),
      };
    },
  },
];

/* Which preset a range corresponds to, so the chosen chip stays lit after a
   reload and goes dark the moment somebody edits a date by hand. */
export const matchPreset = (start, end) => {
  if (!start || !end) return null;
  const same = (a, b) =>
    a && b && new Date(a).toDateString() === new Date(b).toDateString();
  const hit = DATE_PRESETS.find((p) => {
    const r = p.range();
    return same(r.start, start) && same(r.end, end);
  });
  return hit ? hit.key : null;
};

/* Pending means "still needs somebody to do something". Not Possible is work
   handed back as undoable — counting it as pending would show a backlog that
   never clears. Same rule sp_Sky_DashBoard_TeamWise applies, so the reports
   and the dashboard cannot disagree about what is outstanding. */
const DONE_STATUSES = new Set(["complete", "completed", "not possible"]);

export const isPendingStatus = (status) =>
  !DONE_STATUSES.has(String(status || "").trim().toLowerCase());

export const lastSevenDays = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 6);
  return { start, end };
};

export const getAvatarColor = (name) => {
  if (!name) return "#0d6efd"; // Default blue
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  // Using HSL allows us to control saturation and lightness for better readability
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 65%, 45%)`;
};



// Converts minutes (70) to "01 : 10" format
export const minutesToHHMM = (totalMinutes) => {
  if (!totalMinutes || isNaN(totalMinutes)) return "00 : 00";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")} : ${String(minutes).padStart(2, "0")}`;
};


export const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";

  // FIX: If the date string is missing 'Z' (UTC marker), add it manually.
  // This tells the browser "This time is in UTC, please convert it to my local time."
  if (typeof dateStr === "string" && !dateStr.endsWith("Z") && !dateStr.includes("+")) {
    dateStr += "Z";
  }

  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    // timeZone: "UTC", // REMOVED: Let browser use system local time
  });
};

// --- HELPER: Format Date for API Payload (Convert Local back to UTC) ---


export const calculateDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return null;

  const start = moment.tz(startDate, TIME_ZONE);
  const end = moment.tz(endDate, TIME_ZONE);

  // total difference in minutes
  const totalMinutes = end.diff(start, "minutes");

  // hours & minutes split
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return {
    totalMinutes,
    hours,
    minutes,
    display:
      hours > 0
        ? `${hours} hr ${minutes} min`
        : `${minutes} min`,
  };
};

export const getTimeDiffInSeconds = (
  startDate,
  endDate,
  timezone = TIME_ZONE
) => {
  if (!startDate || !endDate) return 0;

  const start = moment.tz(startDate, timezone);
  const end = moment.tz(endDate, timezone);

  if (!start.isValid() || !end.isValid()) return 0;

  return end.diff(start, "seconds");
};

export const secondsToHoursMinutes = (totalSeconds) => {
  if (!totalSeconds || totalSeconds < 0) return "00:00";

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

// export const formatWithTimeZone = (date, format) => {
//   if (!date) return "-";

//   return moment
//     .utc(date)
//     .tz(TIME_ZONE)
//     .format(format);
// };
export const formatWithTimeZone = (
  value,
  format,
  time = null,
  isNextDay = false
) => {
  if (!value) return "-";

  // Existing behaviour (Display)
  if (!time) {
    return moment
      .utc(value)
      .tz(TIME_ZONE)
      .format(format);
  }
  const baseDate = isNextDay ? "1900-01-02" : "1900-01-01";

  return `${baseDate} ${time}`;
};

export const formatTime12Hour = (time) => {
  if (!time) return "-";

  return moment(time, ["HH:mm", "HH:mm:ss"]).format("hh:mm:A");
};

export const formatTime = (time) => {
  if (!time) return null;
  return `${time}:00`;
};

export const formatApiDateTime = (time) => {
  if (!time) return null;

  return `${moment().tz("Asia/Kolkata").format("YYYY-MM-DD")} ${time}:00`;
};
export const formatTime24 = (time) => {
  if (!time) return "";

  return moment
    .tz(time, ["HH:mm"], TIME_ZONE)
    .format("HH:mm");
};

// common.js
export const convert24To12 = (time) => {
  if (!time) return "";

  const [hour, minute] = time.split(":");
  const h = Number(hour);

  const ampm = h >= 12 ? "PM" : "AM";
  const displayHour = h % 12 || 12;

  return `${displayHour}:${minute} ${ampm}`;
};
export const calculateWorkingTime = (
  clockIn,
  clockOut,
  attendanceDate,
  timezone = TIME_ZONE
) => {
  if (!clockIn || !clockOut || !attendanceDate) {
    return {
      totalMinutes: "",
      totalHours: "",
    };
  }

  let start = moment.tz(`${attendanceDate} ${clockIn}`, "YYYY-MM-DD HH:mm:ss", timezone);
  let end = moment.tz(`${attendanceDate} ${clockOut}`, "YYYY-MM-DD HH:mm:ss", timezone);
  if (end.isSameOrBefore(start)) { end.add(1, "day"); }
  const totalMinutes = end.diff(start, "minutes");
  return {
    totalMinutes,
    totalHours: moment
      .utc(totalMinutes * 60000)
      .format("HH:mm"),
  };
};


export const normalizeTotalTimeFilter = (value) => {
    if (!value) return "";

    let v = value.trim().toLowerCase();

    // Remove 'm'
    v = v.replace(/m/g, "");

    // Replace 'h:' with ':'
    v = v.replace(/h:/g, ":");

    // Remove remaining 'h'
    v = v.replace(/h/g, "");

    return v.trim();
};